<template>
  <div class="text-area-standard-box">
    <div class="text-area-standard-box__label">
      {{ label }}
    </div>
    <div class="wrapper">
      <textarea
        ref="textAreaRef"
        v-model="inputValue"
        class="text-area-standard"
        :placeholder="placeHolderText"
        @blur="emits('emitBlur')"
        @input="emitInputValue"
      />
      <div class="bottom-container">
        <ButtonPrimary
          class="bottom-container__button"
          :buttonText="buttonText"
          :disabled="disabled"
          size="small"
          @onClicked="emitButtonClick"
        />
      </div>
    </div>
    <div v-if="helpText.length" class="text-area-standard-box__help-text">
      {{ helpText }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'

const props = withDefaults(
  defineProps<{
    buttonText?: string
    disabled?: boolean
    helpText?: string
    hideIconX?: boolean
    label?: string
    placeHolderText?: string
    prefilledText?: string
  }>(),
  {
    buttonText: '',
    disabled: false,
    helpText: '',
    hideIconX: false,
    label: '',
    placeHolderText: '',
    prefilledText: ''
  }
)

interface EMITS {
  (e: 'emitInput', inputValue: string | number): void
  (e: 'emitBlur'): void
  (e: 'onClicked', inputValue: string | number): void
}
const emits = defineEmits<EMITS>()
const textAreaRef = ref<HTMLElement | null>(null)
const inputValue = ref<string | number>('')
const prefilledText = ref<string>(props.prefilledText)
const showIconXValue = ref<string>('')

function clearInputValue() {
  inputValue.value = ''
}
function emitButtonClick() {
  emits('onClicked', inputValue.value)
}
function emitInputValue() {
  emits('emitInput', inputValue.value)
}
function showIconX() {
  showIconXValue.value = 'show'
}

watch(inputValue, (newVal) => {
  if (typeof newVal === 'string' && newVal.length >= 1 && !props.hideIconX) showIconX()
})
if (prefilledText.value?.length) inputValue.value = prefilledText.value
watch(prefilledText, (newVal) => {
  if (newVal?.length) inputValue.value = newVal
})

onMounted(() => {
  if (prefilledText.value?.length) inputValue.value = prefilledText.value
})

defineExpose({
  clearInputValue
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/mixins.scss';
.wrapper {
  display: flex;
  background: variables.$color-pure-white;
  border-radius: 12px;
  border: 1px solid variables.$color-greyscale-secondary;
  flex-direction: column;
  height: 152px;
  justify-content: space-between;
  padding: 12px 0 0;
  overflow: hidden;
  width: 100%;
  &:focus {
    border: variables.$border-width-xsmall solid variables.$color-primary;
    outline: variables.$border-width-medium solid variables.$color-primary-light;
    // Outline workaround for iPhone/Safari
    @supports (-webkit-overflow-scrolling: touch) {
      box-shadow: 0 0 0 variables.$border-width-medium variables.$color-primary-light;
      outline: none;
      -webkit-appearance: none;
    }
    // Outline workaround for iPad/Safari
    @media not all and (min-resolution: 0.001dpcm) {
      box-shadow: 0 0 0 variables.$border-width-medium variables.$color-primary-light;
      outline: none;
      -webkit-appearance: none;
    }
  }
}
.bottom-container {
  border-top: 1px solid #e0e0e0;
  display: flex;
  height: auto;
  justify-content: space-between;
  padding: 8px 8px 8px 16px;
  &__button {
    margin-left: auto;
  }
}
.text-area-standard {
  @include font-mixins.desktop-bold-paragraph;
  @include mixins.scrollbar-vertical;
  border: none;
  color: variables.$color-greyscale-content;
  height: 100%;
  margin-bottom: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 16px;
  resize: none;
  width: 100%;
  &:focus {
    outline: none;
  }
}
.text-area-standard-box {
  display: flex;
  flex-direction: column;
  gap: variables.$gap-large;
  &__label {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
    display: flex;
    flex-direction: row;
    width: fit-content;
    gap: variables.$gap-large;
  }
  &__help-text {
    @include font-mixins.desktop-bold-label;
  }
}
</style>
